import { Component, EventEmitter, Output, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldService } from '../../../core/data-services/field.service';
import { CustomerService } from '../../../core/data-services/customer.service';
import { Field } from '../../../core/data-models/field.model';
import { FieldRequest } from '../../../core/request-models/field-request.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-field-quick-add',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaxLengthDirective],
  templateUrl: './field-quick-add.component.html',
  styleUrls: ['./field-quick-add.component.scss']
})
export class FieldQuickAddComponent {
  private fb = inject(FormBuilder);
  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);
  private destroyRef = inject(DestroyRef);

  @Output() fieldCreated = new EventEmitter<Field>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  isSaving = signal(false);
  isOpen = signal(false);

  // Customer options for dropdown
  customerOptions: { id: string; name: string }[] = [];

  // Common crop types
  cropTypes = [
    'Corn',
    'Soybeans',
    'Wheat',
    'Cotton',
    'Rice',
    'Sorghum',
    'Alfalfa',
    'Hay',
    'Barley',
    'Oats',
    'Apple Trees',
    'Citrus Trees',
    'Grapes',
    'Vegetables',
    'Other'
  ];

  constructor() {
    this.initForm();
  }

  get isOtherCropType(): boolean {
    return this.form?.get('cropType')?.value === 'Other';
  }

  private initForm(): void {
    this.form = this.fb.group({
      customerId: [''],
      name: ['', [Validators.required, Validators.minLength(2)]],
      acreage: ['', [Validators.required, Validators.min(0.1)]],
      cropType: ['', Validators.required],
      customCropType: [''],
      latitude: ['', [Validators.min(-90), Validators.max(90)]],
      longitude: ['', [Validators.min(-180), Validators.max(180)]]
    });

    // Clear customCropType value and errors when switching away from "Other"
    this.form.get('cropType')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value !== 'Other') {
        this.form.get('customCropType')?.reset();
      }
    });
  }

  open(): void {
    this.customerOptions = this.customerService.getCustomerOptions();
    this.isOpen.set(true);
    this.form.reset();
  }

  close(): void {
    this.isOpen.set(false);
    this.form.reset();
    this.cancelled.emit();
  }

  onSubmit(): void {
    // Trim name before validation so whitespace-only input is caught
    const nameControl = this.form.get('name');
    if (nameControl?.value) {
      nameControl.setValue(nameControl.value.trim());
    }

    // Validate custom crop type when "Other" is selected
    if (this.form.get('cropType')?.value === 'Other' && !this.form.get('customCropType')?.value?.trim()) {
      this.form.get('customCropType')?.markAsTouched();
      this.form.get('customCropType')?.setErrors({ required: true });
      return;
    }

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSaving.set(true);

    const formData = this.form.getRawValue();
    const effectiveCropType = formData.cropType === 'Other' && formData.customCropType?.trim()
      ? formData.customCropType.trim()
      : formData.cropType;
    const dto: FieldRequest = {
      name: formData.name,
      acreage: parseFloat(formData.acreage),
      cropType: effectiveCropType,
      customerId: formData.customerId || undefined,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null
    };

    // Call API to create field
    this.fieldService.createField(dto).then(newField => {
      this.isSaving.set(false);
      this.isOpen.set(false);
      this.form.reset();
      if (newField) {
        this.fieldCreated.emit(newField);
      }
    }).catch(error => {
      console.error('Error creating field:', error);
      this.isSaving.set(false);
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }
}
